import { useQuery } from '@tanstack/react-query';
import { apiClient } from '../../lib/api';
import type { QRCodeData } from '../../types/location.types';

// Получение QR кода локации
export const useLocationQR = (locationId: string | undefined, format: 'svg' | 'png' = 'svg') => {
  return useQuery({
    queryKey: ['locations', locationId, 'qr', format],
    queryFn: async () => {
      if (!locationId) throw new Error('Location ID is required');

      if (format === 'png') {
        // Для PNG получаем blob и конвертируем в base64
        const headers = await apiClient.getHeaders();
        const response = await fetch(
          `${apiClient.baseURL}/api/v1/locations/${locationId}/qr?format=png&size=512`,
          {
            headers,
          }
        );

        if (!response.ok) {
          throw new Error('Failed to fetch QR code');
        }

        const blob = await response.blob();
        const base64 = await new Promise<string>((resolve) => {
          const reader = new FileReader();
          reader.onloadend = () => resolve(reader.result as string);
          reader.readAsDataURL(blob);
        });

        return {
          deeplink: '',
          svg: base64,
          format: 'png',
        } as QRCodeData;
      }

      // Для SVG получаем JSON с deeplink и SVG
      const response = await apiClient.getLocationQR(locationId);
      return response as QRCodeData;
    },
    enabled: !!locationId,
    staleTime: 5 * 60 * 1000, // Кэшировать QR на 5 минут
  });
};

// Функция для скачивания QR кода
export const downloadQR = async (
  locationId: string,
  locationName: string,
  format: 'svg' | 'png',
  size = 512
) => {
  try {
    const url = `${apiClient.baseURL}/api/v1/locations/${locationId}/qr?format=${format}&size=${size}`;

    const headers = await apiClient.getHeaders();
    const response = await fetch(url, {
      headers,
    });

    if (!response.ok) {
      throw new Error('Failed to download QR code');
    }

    const blob = await response.blob();
    const downloadUrl = URL.createObjectURL(blob);

    // Создаем ссылку для скачивания
    const link = document.createElement('a');
    link.href = downloadUrl;
    link.download = `qr-${locationName.toLowerCase().replace(/\s+/g, '-')}.${format}`;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);

    // Очищаем URL
    URL.revokeObjectURL(downloadUrl);
  } catch (error) {
    console.error('Error downloading QR code:', error);
    throw error;
  }
};

// Функция для печати QR кода
export const printQR = async (locationId: string, locationName: string) => {
  try {
    const response = await apiClient.getLocationQR(locationId);
    const qrData = response as QRCodeData;

    // Создаем окно для печати
    const printWindow = window.open('', '_blank');
    if (!printWindow) {
      throw new Error('Failed to open print window');
    }

    // HTML для печати
    printWindow.document.write(`
      <!DOCTYPE html>
      <html>
        <head>
          <title>QR код - ${locationName}</title>
          <style>
            body {
              margin: 0;
              padding: 20px;
              font-family: Arial, sans-serif;
              text-align: center;
            }
            .container {
              max-width: 600px;
              margin: 0 auto;
            }
            .qr-container {
              margin: 20px 0;
              display: flex;
              justify-content: center;
            }
            .qr-container svg {
              width: 300px;
              height: 300px;
            }
            .info {
              margin: 20px 0;
              padding: 10px;
              border: 1px solid #ddd;
              border-radius: 8px;
            }
            h1 {
              margin: 0 0 10px 0;
              font-size: 24px;
            }
            p {
              margin: 5px 0;
              color: #666;
            }
            @media print {
              body { margin: 0; }
            }
          </style>
        </head>
        <body>
          <div class="container">
            <h1>${locationName}</h1>
            <div class="qr-container">
              ${qrData.svg}
            </div>
            <div class="info">
              <p><strong>Локация:</strong> ${locationName}</p>
              <p><strong>ID:</strong> ${locationId}</p>
              <p><strong>Дата печати:</strong> ${new Date().toLocaleDateString('ru-RU')}</p>
            </div>
            <p style="margin-top: 30px; font-size: 12px;">
              Отсканируйте QR код в мобильном приложении TCO для отметки посещения
            </p>
          </div>
          <script>
            window.onload = function() {
              window.print();
              window.onafterprint = function() {
                window.close();
              };
            };
          </script>
        </body>
      </html>
    `);

    printWindow.document.close();
  } catch (error) {
    console.error('Error printing QR code:', error);
    throw error;
  }
};